import React from "react";
import moment from "moment";
import { isEqual, find } from "lodash";

import { View, KeyboardAvoidingView } from "react-native";

import AppMessages from "../../../config/AppMessages";
import ReflectionHeader from "./header/ReflectionHeader";
import ReflectionFabButton from "./ReflectionFabButton";

import ReflectionList from "./list/ReflectionList";
import ReflectionBadgeCard from "./ReflectionBadgeCard";
import ReflectionTutorialCard from "./ReflectionTutorialCard";
import TutorialsHelper from "../tutorials/TutorialsHelper";

import { ReflectionProvider } from "./ReflectionContext";
import MenuEditOptionsPanel from "../common/MenuEditOptionsPanel";
import Utils, {
  getStorageItem,
  getStorageKey,
  hasItems,
  navigate,
  isAndroid,
  getElementModel,
  isWeb,
} from "../../../helpers/utils";
import EditElementsView from "./EditElementsView";
import appStyles from "../../appStyles";
import appPages from "../../../appPages";
import Search from "../common/search-bar/Search";
import ReflectionHelper from "./ReflectionHelper";

import TUTORIALS_DATA from "../tutorials/data";
import JournalDialog from "../common/JournalDialog";
import AppTheme from "../AppTheme";
import RoutinesHelper from "../routines/RoutinesHelper";
import JournalHelper from "../journal/JournalHelper";
import ReportWeekSelectorDialog from "./ReportWeekSelectorDialog";
import AutoTrackFabButton from "./AutoTrackFabButton";
import EmptyReflectionPage from "../empty-pages/EmptyReflectionPage";
import UsersHelper from "../users/UsersHelper";
import SettingsHelper from "../more/SettingsHelper";

import VIEW_MODES from "./header/viewModes";
import ReflectionInsights from "./ReflectionInsights";

const SyncInProgressMessage = AppMessages.SyncInProgress;
const DATE_FORMAT = "YYYY-MM-DD";

export default class ReflectionPage extends React.PureComponent {
  constructor(props) {
    super(props);
    this.controller = props.controller;
    this.useElements = props.useElements;
    this.defaultViewMode = VIEW_MODES.insights;
    this.defaultTimeUnit = "week";

    this.settingsStorageKey = SettingsHelper.getPreferencesStorageKey(
      this.controller,
    );

    this.collapsedKey = getStorageKey(this.controller, "COLLAPSED_GROUPS");

    this.state = {
      selectedGroup: null,
      selectedJournalGoal: null,
      showEditView: false,
      selectedPeriod: "",
      reflections: [],
      showBadgeCard: false,
      tutorialStep: 0,
      showTutorials: false,
      showEmptyPage: false,
    };
  }

  componentDidMount = async () => {
    if (this.controller.hasNavigation()) {
      this.focusListenerUnsub = this.props.navigation.addListener(
        "focus",
        () => {
          this.componentDidFocus();
        },
      );
    } else {
      this.componentDidFocus();
    }
  };

  componentWillUnmount = async () => {
    if (this.focusListenerUnsub) this.focusListenerUnsub();
  };

  componentDidFocus = async () => {
    this.initData();
  };

  getArgs() {
    if (this.controller.hasNavigation()) {
      return this.props.route.params?.args;
    } else {
      return this.controller.state.screenArgs?.args?.args;
    }
  }

  checkIfEmptyPage = () => {
    // For new users' empty page
    this.userCreatedTime = UsersHelper.getUserCreatedTime(
      this.controller.state?.settings?.datetime_created,
      this.props.route?.params?.hours,
    );
    const showEmptyPage = UsersHelper.isNewUser(this.userCreatedTime);

    // For mari message
    if (showEmptyPage) this.emptyPageRef = React.createRef();

    return showEmptyPage;
  };

  initData = async () => {
    // Tutorials
    this.initTutorials();

    // New user empty pages
    const showEmptyPage = this.checkIfEmptyPage();

    // Load preferences
    const preferences = (await SettingsHelper.loadData(
      this.controller,
      this.settingsStorageKey,
    )) || { viewMode: this.defaultViewMode, timeUnit: this.defaultTimeUnit };

    const collapsedIds = (await getStorageItem(this.collapsedKey)) || [];

    // Coming from SetupChecklist card
    const onboardingTimeUnit = this.props.route?.params?.timeUnit;

    this.setState(
      (state) => ({
        ...state,
        collapsedIds,
        preferences,
        expandAll: !!onboardingTimeUnit,
        timeUnit: onboardingTimeUnit || preferences.timeUnit,
        viewMode: preferences.viewMode,
        showEmptyPage,
        showTutorialOverlay: showEmptyPage
          ? false
          : !SettingsHelper.wasTutorialOverlaySeen(
              this.controller,
              "reflection",
            ),
      }),
      () => this.loadPageContent(),
    );

    // Get timeUnit if coming from SetupCheckList then clear params
    if (this.props.route?.params?.timeUnit) {
      this.props.navigation?.setParams({ timeUnit: null });
    }
  };

  loadPageContent = () => {
    // After creating new SET go to edit sets view
    const { show_edit_sets_view } = this.getArgs() || {};

    if (show_edit_sets_view) {
      this.setState((state) => ({ ...state, showEditView: true }));
      this.getReflectionDataFromApi({});
    } else {
      this.loadReflections();
    }
  };

  loadReflections = () => {
    const { selectedDate, userSelectedDate } = this.state;

    // Display old data if available to avoid empty screen
    const oldReflectionData = this.controller.state?.reflection;
    if (oldReflectionData) {
      this.setReflectionModel({
        reflectionData: oldReflectionData,
        selectedDate: selectedDate,
        timeUnit: this.state.timeUnit,
      });
    }

    // Check if newer version of data is available
    this.getReflectionDataFromApi({
      timeUnit: this.state.timeUnit,
      userSelectedDate,
    });
  };

  getReflectionDataFromApi = ({
    userSelectedDate,
    timeUnit = "week",
    forceUpdate = false,
  }) => {
    const { viewMode, showTutorials, isAutoTrackMode } = this.state;

    const userDateString =
      typeof userSelectedDate == "string"
        ? userSelectedDate
        : userSelectedDate?.format(DATE_FORMAT);
    const selectedDate = ReflectionHelper.getReflectionSelectedDate({
      viewMode,
      showTutorials,
      userSelectedDate: userDateString,
      timeUnit,
    });

    const params = selectedDate
      ? {
          reflectionDate: moment(selectedDate)
            .add(1, "day")
            .format(DATE_FORMAT),
          time_unit: timeUnit,
          useElements: !!this.useElements,
        }
      : null;

    const oldReflectionData = this.controller.state?.reflection;

    this.setState((state) => ({
      ...state,
      isLoading: true,
      mariMessage: this.state.showEmptyPage ? "" : SyncInProgressMessage,
    }));

    const apiEndpoint = isAutoTrackMode ? "reflection-autotrack" : "reflection";

    this.controller.getApiData(apiEndpoint, params, (reflectionData) => {
      if (!forceUpdate && isEqual(oldReflectionData, reflectionData)) {
        this.setState((state) => ({
          ...state,
          isLoading: false,
          mariMessage: "",
          term: "",
          selectedDate,
          userSelectedDate: userDateString,
          timeUnit,
        }));
        return;
      }

      this.controller.state.reflection_insights = reflectionData; //ugly temp(?) hack
      this.setReflectionModel({
        reflectionData,
        selectedDate,
        timeUnit,
        isFromApi: true,
      });
    });
  };

  setReflectionModel = ({
    reflectionData,
    selectedDate,
    timeUnit = "week",
    isFromApi = false,
  }) => {
    const { collapsedIds } = this.state;

    let reflections = reflectionData.children;
    if (reflections != null) {
      reflections.forEach((r) => this._initCollapsedState(r, collapsedIds));
    }

    //reflections = this.computeMiscData(reflections); //disable Misc adding, done from backend

    this.setState(
      (state) => ({
        ...state,
        isLoading: false,
        reflections,
        header: reflectionData.header,
        selectedDate,
        timeUnit,
        mariMessage: isFromApi ? "" : SyncInProgressMessage,
        term: "",
      }),
      () => {
        if (isFromApi && this.state.showEditView) this.showEditSetsView();
      },
    );
  };

  computeMiscData = (reflections) => {
    reflections.forEach((g) => {
      if (
        (g.item_type == "set" || g.is_core == 1) &&
        g.item_id != "resolution" &&
        hasItems(g.children) &&
        hasItems(g.days) // Skip goals
      ) {
        let miscModel = {
          icon: g.icon,
          item_id: g.item_id,
          item_type: "activity", // hack 'til we migrate elemens
          name: `${g.name} (Misc)`,
          days: [{ hours: 0 }, { hours: 0 }, { hours: 0 }],
        };

        g.days.forEach((day, index) => {
          const dailyCount = g.children.reduce((acc, child) => {
            return child.days ? child.days[index]?.hours + acc : acc;
          }, 0);

          // Found missing time: Logged directly on CoreElement
          if (dailyCount < day.hours) {
            miscModel.hasMissingHours = true;
            miscModel.days[index] = { hours: day.hours - dailyCount };
          }
        });

        g.days.forEach((day, index) => {
          const dailyCount = g.children.reduce((acc, child) => {
            return child.days ? child.days[index]?.hours + acc : acc;
          }, 0);

          // Found missing time: Logged directly on CoreElement
          if (
            dailyCount &&
            day.hours &&
            dailyCount > 0 &&
            dailyCount < day.hours
          ) {
            miscModel.hasMissingHours = true;
            miscModel.days[index] = { hours: day.hours - dailyCount };
          }
        });

        if (miscModel.hasMissingHours) {
          g.children.push(miscModel);
        }
      }
    });

    return reflections;
  };

  _initCollapsedState = (element, collapsedIds) => {
    const { expandAll } = this.state;

    // For nested tags in multiple groups
    const elementId = [element.parent_id, element.item_id]
      .filter((id) => id)
      .join("-");

    element.isExpanded =
      collapsedIds && Array.isArray(collapsedIds)
        ? collapsedIds.includes(elementId)
        : false;

    if (hasItems(element.children)) {
      element.children.forEach((child) => {
        child.parent_id = element.item_id;
        this._initCollapsedState(child, collapsedIds);
      });
    }
  };

  filterByDate = (userSelectedDate, timeUnit) => {
    const { preferences } = this.state;

    SettingsHelper.storeData(this.controller, this.settingsStorageKey, {
      ...preferences,
      timeUnit,
    });

    this.getReflectionDataFromApi({ userSelectedDate, timeUnit });
  };

  // ------------------------------------- FAB -------------------------------------
  addNewCoreElement = () => {
    navigate(this.controller, "rn:edit_element", "changepage", {
      element: getElementModel({ is_core: 1 }),
    });
  };

  syncCalendar = () => {
    const { selectedDate, timeUnit } = this.state;

    let action = {
      action: "sync_calendar",
      day: selectedDate.format(DATE_FORMAT),
      period: timeUnit,
    };

    this.controller.apiService.cardAction(action);
  };

  changeViewMode = (vm) => {
    const { preferences } = this.state;

    // Back to chart/table mode from Reflection header
    const viewMode = vm || preferences.viewMode;

    SettingsHelper.storeData(this.controller, this.settingsStorageKey, {
      ...preferences,
      viewMode,
    });

    this.setState((state) => ({
      ...state,
      viewMode,
      showEmptyPage: false, // From empty reflection page
    }));
  };
  // ------------------------------------- EDIT MENU -------------------------------------
  showEditOptions = (selectedGroup) => {
    const { isAutoTrackMode } = this.state;
    if (isAutoTrackMode) return;

    if (selectedGroup.item_type !== "set") return;
    this.setState((state) => ({ ...state, selectedGroup }));
  };

  closeEditOptions = () => {
    this.setState((state) => ({ ...state, selectedGroup: null }));
  };

  showEditSetsView = () => {
    const { reflections = [] } = this.state;

    const selectedSetIds = this.controller.state?.reflection_root
      ? this.controller.state?.reflection_root
      : reflections
          .filter((r) => r.item_type == "set")
          .map((s) => {
            return s.item_id;
          });

    this.setState((state) => ({
      ...state,
      selectedSetIds,
      showEditSets: true,
    }));
  };

  reloadReflectionData = () => {
    this.setState((state) => ({
      ...state,
      showEditView: false,
      selectedSetIds: [],
      showEditSets: false,
      selectedGroup: null,
    }));
    this.getReflectionDataFromApi({});
  };

  editGroup = () => {
    const { selectedGroup } = this.state;

    this.closeEditOptions();
    this.goToEditElement_V2(selectedGroup);
  };

  deleteGroupFromView = () => {
    const { selectedGroup } = this.state;

    this.closeEditOptions();

    const action = {
      action: "save",
      target: "set_view_entry",
      id: selectedGroup.item_id,
      selected: 0,
      view: "Default",
    };

    this.setState((state) => ({ ...state, isLoading: true }));
    console.log("Save View Entry");
    this.controller.cardActionWithCallback(action, () => {
      this.setState((state) => ({ ...state, isLoading: false }));
      this.reloadReflectionData();
    });
  };

  // ------------------------------------- EDIT REFLECTION -------------------------------------
  onIconClick = () => {
    const { header } = this.state;

    const editDay = header.days ? header.days[0] : null;
    if (!editDay) return;

    this.goToEditPage(
      {
        timeline: header.timeline,
      },
      editDay,
      0,
    );
  };

  goToEditPage = (reflection, day, dayIndex) => {
    const { timeUnit, header, reflections, isAutoTrackMode } = this.state;

    if (isAutoTrackMode) return;

    // Get edit-day
    let editDay = null;
    if (header.days && header.days[dayIndex]) {
      editDay = header.days[dayIndex].gem_day;
    }

    if (!editDay) return;

    let model = {
      timeline: reflection.timeline,
      children: reflection.children,
      goal: reflection.goal,
      edit_time_unit: timeUnit,
      edit_object: reflection.item_type || "time",
      edit_element_id: reflection.item_id,
      edit_day: editDay,
      edit_icon: reflection.icon,
      edit_name: reflection.name,
      edit_notes: day.gem?.text || "",
      edit_gem_mood: day.gem?.mood || "",
      target_hours: day.hours || "",
      edit_id: day.gem?.gem_id || "",
    };

    // Top Core Elements for bubble chart
    let hours = null;
    if (model.edit_object == "time") {
      const { core_elements, days } =
        ReflectionHelper.addCoreElementsHours(reflections);

      model.core_elements = core_elements;
      if (!hasItems(model.timeline.days)) model.timeline.days = days;

      // Routines
      if (hasItems(reflections) && (timeUnit == "day" || timeUnit == "week")) {
        hours = RoutinesHelper.getHoursFromReflections(
          reflections,
          dayIndex,
          timeUnit,
        );
      }
    }

    navigate(this.controller, "rn:sets_reflection_detail", "changepage", {
      reflection: { ...model },
      routineHours: hours,
    });
  };

  // ------------------------------------- NOTES -------------------------------------
  toggleJournalDialog = (item, day, dayIndex) => {
    const { timeUnit, header, isAutoTrackMode } = this.state;
    if (isAutoTrackMode) return;

    let editDay = null;
    if (header.days && header.days[dayIndex]) {
      editDay = header.days[dayIndex].gem_day;
    }

    const model = item
      ? {
          ...item,
          note: day?.gem?.text,
          mood: day?.gem?.mood,
          gem_id: day?.gem?.gem_id,
          hours: day?.hours,
          time_unit: timeUnit,
          day: editDay,
        }
      : null;

    this.setState(
      (state) => ({
        ...state,
        selectedJournalGoal: model,
      }),
      () => {
        // After journal action refresh content
        if (!model) this.loadReflections();
      },
    );
  };

  goToEditElement = (element) => {
    const { isAutoTrackMode } = this.state;
    if (isAutoTrackMode) return;

    this.goToEditElement_V2(element);
  };

  goToEditElement_V2 = (element) => {
    const { isAutoTrackMode } = this.state;

    if (isAutoTrackMode) return;
    const selectedPage = "rn:edit_element";

    const source = this.controller.state?.elements || [];
    const editElement = find(source, (el) => el.id == element.item_id);

    navigate(this.controller, selectedPage, "changepage", {
      element: getElementModel(editElement),
    });
  };

  // ------------------------------------- FILTER -------------------------------------
  filterByKeyword = (reflections, pattern) => {
    if (!hasItems(reflections)) return false;

    let shouldDisplay = false;
    reflections.forEach((r) => {
      const hasVisibileChildren = this.filterByKeyword(r.children, pattern);
      r.isHidden =
        r.item_type == "set" || hasVisibileChildren
          ? false
          : pattern
          ? r.name && !r.name.match(pattern)
          : false;

      if (!r.isHidden) shouldDisplay = true;

      // Expand parents with matching children
      r.hasMatchedChildren = pattern && hasVisibileChildren;

      // Highlight matched element
      r.isSearchMatch = pattern && r.name && r.name.match(pattern);
    });

    return shouldDisplay;
  };

  filterResults = ({ term }) => {
    let filteredReflections = this.state.reflections
      ? [...this.state.reflections]
      : [];

    const pattern = Utils.getSearchPattern(term);

    this.filterByKeyword(filteredReflections, pattern);

    this.setState((state) => ({
      ...state,
      isLoading: false,
      reflections: [...filteredReflections],
    }));
  };

  // ------------------------------------- TUTORIALS -------------------------------------
  initTutorials = () => {
    const showTutorials = (this.controller?.state?.showTutorials || {})[
      TutorialsHelper.STORAGE_KEYS.reflection
    ];

    if (showTutorials) {
      const { tutorialDone } = this.getArgs() || {};

      const nextStep = tutorialDone
        ? this.state.tutorialStep == 3
          ? 4 // logged new journal
          : this.state.tutorialStep // back from edit period journal
        : 1;

      this.loadTutorial(nextStep);
    } else {
      this.setState((state) => ({
        ...state,
        tutorialMessage: "",
        tutorialStep: 0,
        showTutorials: false,
        mariAction: {},
      }));
    }
  };

  loadTutorial = (step = 1) => {
    const tutorial =
      step >= 0 ? TUTORIALS_DATA["reflection"][step - 1] || {} : {};

    this.setState((state) => ({
      ...state,
      showTutorials: true,
      tutorialStep: step,
      hideActions: !!step,
      showBadgeCard: step == 5,
      tutorialMessage: tutorial.message,
      mariAction: tutorial.action
        ? {
            text: tutorial.action.text,
            onPress: () => this.loadTutorial(step + 1),
          }
        : {},
    }));
  };

  onBadgePress = () => {
    this.setState(
      (state) => ({
        ...state,
        tutorialStep: 0,
        mariMessage: "",
        mariAction: {},
        showBadgeCard: false,
      }),
      () => {
        this.controller.toggleTutorialsMode();
        TutorialsHelper.saveTutorialStatus(
          this.controller,
          { reflection: true },
          navigate(this.controller, "rn:home_card", {}),
        );
      },
    );
  };

  howToReflect = () => {
    this.controller.toggleTutorialsMode({
      [TutorialsHelper.STORAGE_KEYS.track]: true,
    });

    navigate(this.controller, "onboarding_template", {});
  };

  // ------------------------------------- ROUTINES -------------------------------------
  createNewRoutine = () => {
    const { reflections, timeUnit, selectedDate } = this.state;

    if (hasItems(reflections) && (timeUnit == "day" || timeUnit == "week")) {
      const intervalLength = timeUnit == "week" ? 7 : 1;

      this.controller.getApiData(
        "timeslots",
        {
          timeslotsDate:
            selectedDate != null ? selectedDate.format(DATE_FORMAT) : null,
          days: intervalLength,
        },
        (ts) => {
          let model = RoutinesHelper.getNewRoutineModel();
          model.selectedDate = selectedDate;
          model.time_interval = timeUnit;
          model.hours = JSON.stringify(
            RoutinesHelper.getHoursFromReflections(reflections, 0, timeUnit),
          );

          // TODO: for now, dont send timeslots for week routine (backend handles it)
          if (timeUnit == "day") {
            model.timeslots = JournalHelper.filterTimeslotsByInterval(
              ts,
              selectedDate,
              intervalLength,
            );
          }

          navigate(this.controller, "rn:edit_routine", "changepage", {
            routine: model,
          });
        },
      );
    }
  };

  // ------------------------------------- REPORTS -------------------------------------
  toggleCreateReportDialog = (isOpen = false) => {
    this.setState((state) => ({ ...state, isWeekSelectorOpen: isOpen }));
  };

  extractRows = (item, output, parents) => {
    const row = [
      item.name,
      item.days && item.days.length > 0 ? item.days[0].hours : "",
      item.days && item.days.length > 1 ? item.days[1].hours : "",
      item.days && item.days.length > 2 ? item.days[2].hours : "",
      parents.join(";"),
    ];

    const newParents = parents.slice(0);
    newParents.push(item.name);

    output.push(row);
    if (item.children) {
      item.children.forEach((c) => {
        this.extractRows(c, output, newParents);
      });
    }
  };

  downloadCsv = () => {
    if (!isWeb) return;

    const data = [];
    const header = ["Name", "Current", "T-1", "T-2", "Parents"];
    data.push(header);

    if (this.state.reflections) {
      this.state.reflections.forEach((c) => {
        const parents = [];
        this.extractRows(c, data, parents);
      });
    }

    const { selectedDate } = this.state;
    const user =
      this.controller.state.impersonate || this.controller.state.currentUser;
    const csvName =
      "mana-reflection-" +
      this.state.timeUnit +
      "-" +
      selectedDate?.format(DATE_FORMAT) +
      ".csv";

    //console.log(content);
    const content = data.join("\n");
    const csvData = new Blob([content], { type: "text/csv" });
    var url = URL.createObjectURL(csvData);
    //const url = "data:text/csv;base64," + content.toString(Base64);
    var hlink = document.createElement("a");
    hlink.href = url;
    hlink.download = csvName;
    hlink.click();
  };

  createNewReport = (reportSelectedDate) => {
    const { selectedDate, timeUnit, viewMode, showEmptyPage } = this.state;

    if (showEmptyPage && this.emptyPageRef?.current) {
      this.emptyPageRef.current.showMariMessage(
        AppMessages.EmptyReflectionPageMariMessage,
      );
      return;
    }

    // For table view show week selector dialog
    if (viewMode == VIEW_MODES.tableView && !reportSelectedDate) {
      return this.toggleCreateReportDialog(true);
    }

    // For table view use the user selected report date; for chartview use current view date
    const reportDate = reportSelectedDate || selectedDate?.format(DATE_FORMAT);

    // Close dialog
    this.toggleCreateReportDialog(false);

    if (reportDate != null) {
      navigate(this.controller, appPages.reportsPage, "changepage", {
        args: {
          selectedDate: reportDate,
          timeUnit,
        },
      });
    }
  };

  // ------------------------------------- EMPTY STATE -------------------------------------
  closeTutorialOverlay = () => {
    this.setState((state) => ({ ...state, showTutorialOverlay: false }));
    SettingsHelper.updateTutorialStatus(this.controller, { reflection: true });
  };

  aboutThisScreen = () => {
    this.setState((state) => ({ ...state, showTutorialOverlay: true }));
  };

  // ------------------------------------- AUTOTRACK MODE -------------------------------------
  toggleAutoTrackMode = (isAutoTrackMode) => {
    this.setState(
      (state) => ({ ...state, isAutoTrackMode }),
      () => {
        this.loadReflections();
      },
    );
  };

  render() {
    const {
      header,
      timeUnit,
      reflections,
      selectedGroup,
      selectedJournalGoal,
      mariMessage,
      isLoading,
      selectedSetIds,
      showEditSets,
      viewMode: vmode,
      showTutorials,
      tutorialStep,
      tutorialMessage,
      mariAction,
      showBadgeCard,
      tutorialDone,
      isWeekSelectorOpen,
      isAutoTrackMode,
      showEmptyPage,
      showTutorialOverlay,
      expandAll,
      selectedDate,
    } = this.state;

    const { show_admin_tools: isAdmin } = this.controller.state?.settings || {};
    const viewMode = showTutorials ? VIEW_MODES.chartView : vmode;

    const selectedPeriod = selectedDate
      ? ReflectionHelper.getReflectionHeaderPeriod(
          selectedDate.format(DATE_FORMAT),
          timeUnit,
        )
      : "";

    const availableReflections =
      reflections != null ? reflections.filter((r) => !r.isHidden) : {};

    const reflectionContext = {
      controller: this.controller,
      totalCount: availableReflections?.length,
      viewMode,
      timeUnit,
      isAdmin,
      tutorialStep,
      isAutoTrackMode,
      selectedDate,
      header,
      onHoursSelected: this.goToEditPage,
      showEditOptions: this.showEditOptions,
      onHoursLongPressed: this.toggleJournalDialog,
      onElementSelected: this.goToEditElement,
      filterByDate: this.filterByDate,
      showReflectionTutorials: showTutorials,
      createNewReport: this.createNewReport,
      goBackOneStep: () => this.toggleAutoTrackMode(false),
      toggleAutoTrackMode: this.toggleAutoTrackMode,
      changeViewMode: this.changeViewMode,
    };

    return (
      <ReflectionProvider value={reflectionContext}>
        {/* {showEmptyPage || showTutorialOverlay ? ( // 2022.04.21 Removed gating counter for new users (temp) */}
        {showTutorialOverlay ? (
          <EmptyReflectionPage
            ref={this.emptyPageRef}
            userCreatedTime={this.userCreatedTime}
            header={header}
            title={"Reflection"}
            subTitle={""}
            controller={this.controller}
            openOverlay={showTutorialOverlay}
            closeTutorialOverlay={this.closeTutorialOverlay}
          />
        ) : showEditSets ? (
          <EditElementsView
            controller={this.controller}
            selectedSetIds={selectedSetIds}
            goBack={this.reloadReflectionData}
          />
        ) : (
          <View
            style={[
              appStyles.container,
              { backgroundColor: AppTheme.containerBg },
            ]}
          >
            <KeyboardAvoidingView
              style={appStyles.kbView}
              behavior={"height"}
              keyboardVerticalOffset={isAndroid ? 70 : 0}
            >
              <ReflectionHeader
                header={header}
                title={"Reflection"}
                subTitle={selectedPeriod}
                controller={this.controller}
                isLoading={isLoading}
                onIconClick={this.onIconClick}
              />

              {viewMode == VIEW_MODES.insights ? (
                <ReflectionInsights
                  selectedDate={
                    typeof selectedDate == "string"
                      ? selectedDate
                      : selectedDate?.format(DATE_FORMAT)
                  }
                  timeUnit={timeUnit}
                  controller={this.controller}
                  changeViewMode={this.changeViewMode}
                />
              ) : showTutorials && !hasItems(availableReflections) ? (
                <ReflectionTutorialCard onPress={this.howToReflect} />
              ) : (
                <ReflectionList
                  hasChart={viewMode == VIEW_MODES.chartView}
                  reflections={availableReflections}
                  controller={this.controller}
                  source={"reflection"}
                  expandAll={expandAll}
                />
              )}

              <MenuEditOptionsPanel
                isPanelActive={!!selectedGroup}
                close={this.closeEditOptions}
                options={[
                  {
                    text: "Edit Element",
                    action: this.editGroup,
                  },
                  {
                    text: "Edit View",
                    action: this.showEditSetsView,
                  },
                  {
                    text: "Remove Core Element from View",
                    action: this.deleteGroupFromView,
                    isDelete: true,
                  },
                ]}
              />

              {/* FAB */}
              <View style={[appStyles.footerOptions]}>
                {isAutoTrackMode ? (
                  <AutoTrackFabButton
                    style={{ bottom: showTutorials ? 0 : 42 }}
                    viewManualMode={() => this.toggleAutoTrackMode(false)}
                  />
                ) : viewMode == VIEW_MODES.insights ? (
                  <ReflectionFabButton
                    style={{
                      bottom:
                        showTutorials || viewMode == VIEW_MODES.insights
                          ? 0
                          : 42,
                    }}
                    viewMode={viewMode}
                    timeUnit={timeUnit}
                    changeViewMode={this.changeViewMode}
                    createNewReport={this.createNewReport}
                    createNewRoutine={
                      timeUnit == "day" || timeUnit == "week"
                        ? this.createNewRoutine
                        : null
                    }
                    mariOptions={{
                      mariMessage: tutorialMessage || mariMessage,
                      hideActions: showTutorials && !tutorialDone,
                      mariAction,
                      showTutorials,
                    }}
                  />
                ) : (
                  <ReflectionFabButton
                    style={{
                      bottom:
                        showTutorials || viewMode == VIEW_MODES.insights
                          ? 0
                          : 42,
                    }}
                    showEditSetsView={this.showEditSetsView}
                    viewMode={viewMode}
                    isAdmin={isAdmin}
                    changeViewMode={this.changeViewMode}
                    addNewCoreElement={this.addNewCoreElement}
                    syncCalendar={this.syncCalendar}
                    aboutThisScreen={this.aboutThisScreen}
                    timeUnit={timeUnit}
                    createNewRoutine={
                      timeUnit == "day" || timeUnit == "week"
                        ? this.createNewRoutine
                        : null
                    }
                    createNewReport={this.createNewReport}
                    downloadCsv={isWeb ? this.downloadCsv : null}
                    mariOptions={{
                      mariMessage: tutorialMessage || mariMessage,
                      hideActions: showTutorials && !tutorialDone,
                      mariAction,
                      showTutorials,
                    }}
                    viewAutoTrackMode={
                      isAdmin ? () => this.toggleAutoTrackMode(true) : null
                    }
                  />
                )}

                {/* Search */}
                {viewMode !== VIEW_MODES.insights && !showEmptyPage ? (
                  <Search
                    placeholder="Search elements"
                    controller={this.controller}
                    filterResults={this.filterResults}
                    inputStyle={{ backgroundColor: "#FFF" }}
                  />
                ) : null}
              </View>

              {/* Journal popup */}
              {selectedJournalGoal ? (
                <JournalDialog
                  type={"reflection"}
                  item={selectedJournalGoal}
                  controller={this.controller}
                  close={() => this.toggleJournalDialog(null)}
                />
              ) : null}
            </KeyboardAvoidingView>

            {/* Badge Card */}
            <ReflectionBadgeCard
              isOpen={showBadgeCard}
              onPress={this.onBadgePress}
            />

            {/* Report week selector popup */}
            {isWeekSelectorOpen ? (
              <ReportWeekSelectorDialog
                days={header?.days}
                timeUnit={timeUnit}
                onDateSelected={this.createNewReport}
                close={() => this.toggleCreateReportDialog()}
              />
            ) : null}
          </View>
        )}
      </ReflectionProvider>
    );
  }
}
